import {v4 as uuidv4} from 'uuid';

import {Bugsnag} from '~/foundation/Bugsnag/Bugsnag';
import {Monorail} from '~/foundation/Monorail/Monorail';
import {getTrekkieAttributes} from '~/foundation/Monorail/utils';
import {SendImmediatelyOpenTelemetryClient} from '~/foundation/OpenTelemetry/SendImmediatelyOpenTelemetryClient';
import type {
  MetricOptions,
  TelemetryMetricId,
} from '~/foundation/OpenTelemetry/types';
import {createExporter} from '~/foundation/OpenTelemetry/utils';
import type {OAuthParams} from '~/types/oauth';
import {createCustomElement} from '~/utils/customElement';
import {isoDocument} from '~/utils/document';
import {getFeatures} from '~/utils/features';
import {isoNavigator} from '~/utils/navigator';
import {isoWindow} from '~/utils/window';

import initializeShopLoginInput from './initShopLoginInput/initShopLoginInput';

export const defaultAttributes: Required<OAuthParams> = {
  clientId: '',
  codeChallenge: '',
  codeChallengeMethod: '',
  redirectType: 'top_frame',
  redirectUri: '',
  responseMode: 'query',
  responseType: 'code',
  scope: 'openid email pay:session_token profile',
  state: '',
};

interface ShopNCAInitializerProps {
  attributes: OAuthParams;
  hiddenClassName: string;
  inputSelector: string;
  inputWrapperSelector: string;
  loaderSelector: string;
  unixTimestamp: number;
}

export async function initShopForNewCustomerAccounts({
  attributes: clientProvidedAttributes,
  hiddenClassName,
  inputSelector,
  inputWrapperSelector,
  loaderSelector,
  unixTimestamp,
}: ShopNCAInitializerProps) {
  const analyticsContext = 'loginWithShopSelfServe';
  const analyticsTraceId = uuidv4();
  const flow = 'default';

  const {notify} = new Bugsnag('ShopLoginButton');
  const opentelClient = new SendImmediatelyOpenTelemetryClient({
    exporter: createExporter(),
  });
  const recordCounter = (
    metricId: TelemetryMetricId,
    options: MetricOptions = {},
  ) => {
    const {attributes, unit, value = 1} = options;
    opentelClient.counter({
      attributes: {
        beta: true,
        feature: 'NewCustomerAccountPasskeys',
        ...attributes,
      },
      name: metricId,
      value,
      unit,
    });
  };
  const monorail = new Monorail({
    analyticsData: {
      analyticsContext,
      analyticsTraceId,
      flow,
      shopId: (await getTrekkieAttributes('shopId')).shopId,
    },
    devMode: false,
    notify,
    recordCounter,
  });

  const attributes = {
    ...defaultAttributes,
    ...clientProvidedAttributes,
  };

  monorail.produceMonorailEvent({
    event: {
      schemaId: 'shop_js_ui_shop_login_first_time_render/1.0',
      payload: {
        duration: new Date().getTime() - unixTimestamp,
        shopLoginVersion: 'sdk-rendered',
        url: isoWindow.location.href,
        userAgent: isoNavigator.userAgent,
      },
    },
  });

  let shopLoginButton =
    isoDocument.querySelector<HTMLElement>('shop-login-button');

  if (!shopLoginButton) {
    shopLoginButton = createCustomElement('shop-login-button');
    shopLoginButton.setAttribute('analytics-context', analyticsContext);
    shopLoginButton.setAttribute('analytics-trace-id', analyticsTraceId);
    shopLoginButton.setAttribute('auto-open', 'true');
    shopLoginButton.setAttribute('client-id', attributes.clientId);
    shopLoginButton.setAttribute('code-challenge', attributes.codeChallenge);
    shopLoginButton.setAttribute(
      'code-challenge-method',
      attributes.codeChallengeMethod,
    );
    shopLoginButton.setAttribute('consent-challenge', 'true');
    shopLoginButton.setAttribute('disable-sign-up', 'true');
    shopLoginButton.setAttribute('email-input-selector', inputSelector);
    shopLoginButton.setAttribute('flow', flow);
    shopLoginButton.setAttribute('hide-button', 'true');
    shopLoginButton.setAttribute('id', 'hidden-button-identity');
    shopLoginButton.setAttribute('redirect-type', 'top_frame');
    shopLoginButton.setAttribute('redirect-uri', attributes.redirectUri);
    shopLoginButton.setAttribute('response-mode', attributes.responseMode);
    shopLoginButton.setAttribute('response-type', attributes.responseType);
    shopLoginButton.setAttribute('scope', attributes.scope);
    shopLoginButton.setAttribute('state', attributes.state);
  }

  const shouldInitShopLoginInput =
    await isoWindow?.PublicKeyCredential?.isConditionalMediationAvailable?.();

  const loader = isoDocument.querySelector<HTMLElement>(loaderSelector);
  const target = isoDocument.querySelector<HTMLInputElement>(inputSelector);
  const targetWrapper =
    isoDocument.querySelector<HTMLElement>(inputWrapperSelector);

  if (shouldInitShopLoginInput) {
    initializeShopLoginInput({
      analyticsContext,
      analyticsTraceId,
      attributes,
      flow,
      hiddenClassName,
      loader,
      onLoaded: async () => {
        monorail.produceMonorailEvent({
          event: {
            schemaId: 'shop_js_ui_shop_login_first_time_render/1.0',
            payload: {
              duration: new Date().getTime() - unixTimestamp,
              shopLoginVersion: 'iframed-input-rendered',
              url: isoWindow.location.href,
              userAgent: isoNavigator.userAgent,
            },
          },
        });

        /**
         * Set this attribute after the input iframe finishes loading, because the onLoaded function checks
         * whether it has loaded fast enough. If it has not, it removes itself from the DOM, and we risk
         * leaving the modal anchored to a non-existent element.
         */
        if (isoDocument.getElementById('shop-login-email-input-iframe')) {
          shopLoginButton.setAttribute(
            'anchor-to',
            '#shop-login-email-input-iframe',
          );
        }
      },
      target,
      targetWrapper,
    });
  } else {
    loader?.dispatchEvent(new Event('inputwillnotload'));
  }

  shopLoginButton.setAttribute('anchor-to', '#account_email');

  const features = getFeatures();
  Object.entries(features).forEach(([feature, value]) => {
    shopLoginButton.setAttribute(feature, value);
  });

  targetWrapper?.parentNode?.insertBefore(shopLoginButton, targetWrapper);

  return shopLoginButton;
}
